/**
 * Componente para exibir mensagens de imagem no chat
 * 
 * Suporta: thumbnail, caption, clique para ampliar,
 * loading state e fallback para imagem quebrada
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { ImageOff, Loader2 } from 'lucide-react';

interface ImageMessageBubbleProps {
  /** URL ou caminho relativo da imagem */
  url: string;
  /** Legenda da imagem (opcional) */
  caption?: string | null;
  /** URL da thumbnail (opcional) */
  thumbnailUrl?: string;
  /** Direção da mensagem (entrada ou saída) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar para abrir lightbox */
  onOpenLightbox?: () => void;
}

export function ImageMessageBubble({
  url,
  caption,
  thumbnailUrl,
  direction,
  onOpenLightbox,
}: ImageMessageBubbleProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Converte caminho relativo para URL completa se necessário
  const imageUrl = getStorageUrl(url) || url;
  const thumbUrl = thumbnailUrl ? getStorageUrl(thumbnailUrl) || thumbnailUrl : undefined;

  // Usa thumbnail se disponível, senão imagem completa
  const displayUrl = thumbUrl || imageUrl;

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleClick = () => {
    if (!hasError && onOpenLightbox) {
      onOpenLightbox();
    }
  };

  return (
    <div className="space-y-1">
      {/* Container da imagem - margin negativa compensa padding da bolha */}
      <div
        className={cn(
          'relative rounded-lg overflow-hidden cursor-pointer',
          'max-w-[320px] max-h-[320px] min-w-[100px]',
          '-mx-2.5 md:-mx-4', // Margin negativa para bordas finas estilo WhatsApp
          'inline-block',
          'bg-muted/50'
        )}
        onClick={handleClick}
      >
        {/* Loading skeleton */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error state */}
        {hasError ? (
          <div className="flex flex-col items-center justify-center p-8 bg-muted">
            <ImageOff className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="text-xs text-muted-foreground">
              Imagem não disponível
            </span>
          </div>
        ) : (
          <img
            src={displayUrl}
            alt={caption || 'Imagem'}
            className={cn(
              'block max-w-full max-h-[320px] w-auto h-auto object-contain',
              'transition-opacity duration-200',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            style={{ maxWidth: 320, maxHeight: 320 }}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}

        {/* Hover overlay */}
        {!hasError && !isLoading && (
          <div 
            className={cn(
              'absolute inset-0 bg-background/20 opacity-0',
              'hover:opacity-100 transition-opacity',
              'flex items-center justify-center'
            )}
          >
            <span className="text-xs text-foreground/80 bg-background/60 px-2 py-1 rounded">
              Clique para ampliar
            </span>
          </div>
        )}
      </div>

      {/* Caption */}
      {/* Caption - cores fixas WhatsApp para outbound */}
      {caption && (
        <p 
          className={cn(
            'text-sm whitespace-pre-wrap break-words', 
            direction !== 'outbound' && 'text-foreground'
          )}
          style={direction === 'outbound' ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
